// Worth a look on the League page (SCENARIOS E1, DIRECTION U27,
// PLAN-2026-10-27-worth-a-look §6): a tile for the first of each position the
// lineup starts, as a small card in his club's color with a flame, the next men
// up a line each, and the way to the page. The tiles swipe on a phone, as the
// week's games do, and sit two or three to a row elsewhere.
//
// Facts, never advice: who, his club and his figure, and the button opens the
// lists rather than adding anybody.

import { Link } from 'react-router-dom';
import { failureOf, valueOf, type Snapshot, type WorthALookView } from '@fsp/domain';
import { PositionPill, ProTeamCode } from '../../kit';
import { CardRow } from '../../kit/cards';
import { Icon } from '../../kit/Icon';
import { Button, Caption, Colored, Stack, Text } from '../../kit/ui';
import { proTeamOf } from '../../sports/registry';
import { pointsText, weeksText } from '../shared/worthWords';

/** At most this many of the next men up on the card; the page has the rest. */
const NEXT_ON_CARD = 2;

export function FrontWorth({
  read,
  base,
  numbers,
  phone,
}: {
  read: Snapshot<WorthALookView>;
  base: string;
  /** Standard and up: each tile's figure. Simple keeps the names. */
  numbers: boolean;
  phone: boolean;
}) {
  const view = valueOf(read);
  if (view === null) {
    const failed = failureOf(read);
    return (
      <div className="fsp-front-note">
        <Text tone="secondary">
          {failed === null ? 'Looking over the free agents…' : failed.message}
        </Text>
      </div>
    );
  }
  const to = `${base}/worth-a-look`;
  const started = view.weeks.length > 0;
  const tops = view.positions.flatMap((p) =>
    p.items[0] === undefined ? [] : [{ position: p, item: p.items[0] }],
  );
  const tile = ({ position, item }: (typeof tops)[number]) => {
    const player = item.row.player;
    const club = player.proTeam === null ? undefined : proTeamOf(player.proTeam);
    // NAMED BY WHAT IT SHOWS, the pill's position, his name, club and figure,
    // so a voice command saying what is on it reaches it (WCAG 2.5.3, axe's
    // label-content-name-mismatch, which a label of its own failed).
    return (
      <Link to={to} className="fsp-worth-tile">
        {/* HIS CLUB'S COLOR, as data the look draws (`Colored`). */}
        <Colored
          inline
          className="fsp-worth-tile-stripe"
          colors={club ? { '--fsp-worth-club': club.palette.primary } : {}}
        />
        <span className="fsp-worth-tile-top">
          <PositionPill pos={position.id} size="sm" />
          <Icon name="flame" size="sm" />
        </span>
        <span className="fsp-worth-tile-name">{player.name}</span>
        <span className="fsp-worth-tile-club">
          <ProTeamCode code={player.proTeam} />
        </span>
        {numbers && item.lately !== null ? (
          <span className="fsp-worth-tile-figure">
            <span className="fsp-num">{pointsText(item.lately)}</span> a game
          </span>
        ) : null}
      </Link>
    );
  };

  return (
    <div className="fsp-front-note fsp-front-worth">
      <Stack gap={3}>
        {started ? (
          <Text size="sm" tone="secondary" block>
            On no team · {weeksText(view)}
          </Text>
        ) : (
          <Text tone="secondary" block>
            Who has scored lately shows once a week’s games are final: the players on no team with
            the most points a game, at each position your lineup starts.
          </Text>
        )}
        {!started ? null : tops.length === 0 ? (
          <Text tone="secondary" block>
            Nobody available scored in {weeksText(view)}.
          </Text>
        ) : phone ? (
          <CardRow
            label="Worth a look, a player a position"
            what="player"
            slideSize="42%"
            items={tops.map((t) => ({ key: t.position.id, content: tile(t) }))}
          />
        ) : (
          <div className="fsp-worth-tiles">
            {tops.map((t) => (
              <div key={t.position.id}>{tile(t)}</div>
            ))}
          </div>
        )}
        {view.nextUp.length > 0 ? (
          <Stack gap={1}>
            <Caption>Next man up</Caption>
            <ul className="fsp-worth-front-next">
              {view.nextUp.slice(0, NEXT_ON_CARD).map((item) =>
                item.because.kind === 'next-up' ? (
                  <li key={item.row.player.id}>
                    <PositionPill pos={item.row.player.positions[0] ?? ''} size="sm" />
                    <Text size="sm" block>
                      <strong>{item.row.player.name}</strong>{' '}
                      <ProTeamCode code={item.row.player.proTeam} /> · behind{' '}
                      {item.because.ahead.name}
                      {item.because.ahead.injury !== null
                        ? `, ${item.because.ahead.injury.name}`
                        : ''}
                    </Text>
                  </li>
                ) : null,
              )}
            </ul>
          </Stack>
        ) : null}
        <div>
          <Button size="xs" variant="secondary" to={to} iconRight="next">
            See every position
          </Button>
        </div>
      </Stack>
    </div>
  );
}
